import { useEffect, useState } from "react";
import Twemoji from "react-twemoji";
import { Review } from "./ResultTypes";
import { qs, selectionQuestions } from "./SurveyQuestions";
import "./styles/App.scss";

type Aggregate = {
  question: string;
  emoji: string;
  answers: {
    text: string;
    count: number;
  }[];
};

type Comment = { date: string; text: string };

function Reviews({
  property_reviews,
  other_reviews,
  onOpen,
  scrollToReviews,
}: {
  property_reviews: Review[];
  other_reviews: Review[] | null;
  onOpen: any;
  scrollToReviews: any;
}) {
  const [propertyAgg, setPropertyAgg]: [Aggregate[], any] = useState([]);
  const [otherAgg, setOtherAgg]: [Aggregate[], any] = useState([]);
  const [propertyComments, setPropertyComments] = useState([] as Comment[]);
  const [otherComments, setOtherComments] = useState([] as Comment[]);
  const [selected, setSelected] = useState("this");
  const [propertyItemized, setPropertyItemized] = useState<
    Record<string, number>
  >({});
  const [otherItemized, setOtherItemized] = useState<Record<string, number>>(
    {}
  );

  useEffect(() => {
    if (property_reviews) {
      setPropertyAgg(getStatements(property_reviews));
      setPropertyComments(
        property_reviews
          .filter((rev) => rev.review_text)
          .map((rev) => ({
            date: rev.reported_at,
            text: rev.review_text.trim(),
          }))
          .filter((rev) => rev.text.length)
      );
      setPropertyItemized(getItemized(property_reviews));
    }
    if (other_reviews) {
      let or = other_reviews;
      if (property_reviews)
        or = or.filter(
          (rev) => !property_reviews.find((pr) => pr.id === rev.id)
        );
      setOtherAgg(getStatements(or));
      setOtherComments(
        or
          .filter((rev) => rev.review_text)
          .map((rev) => ({
            text: rev.review_text.trim(),
            date: rev.reported_at,
          }))
          .filter((rev) => rev.text.length)
      );
      setOtherItemized(getItemized(or));
    }
  }, []);

  useEffect(() => {
    if (anyPropertyReviewsExist()) {
      setSelected("this");
    } else {
      setSelected("other");
    }
  }, [propertyItemized, propertyAgg, propertyComments]);

  function getCount(questionId: string, arr: Review[], answerOfNote: number) {
    return arr.reduce((total, rev) => {
      if (rev.answers[questionId] === answerOfNote) {
        total += 1;
      }
      return total;
    }, 0);
  }

  function getStatements(arr: Review[]): Aggregate[] {
    return qs
      .filter((q) => q.answersOfNote)
      .map((q) => ({
        question: q.text,
        emoji: q.emoji,
        answers: q
          .answersOfNote!.map((answerOfNote) => {
            let count = getCount(q.id, arr, answerOfNote);
            return {
              text: q.answers![answerOfNote],
              count,
            };
          })
          .filter((result) => result.count !== 0 && result.text),
      }))
      .filter((agg) => agg.answers.length > 0);
  }

  function getItemized(arr: Review[]): Record<string, number> {
    const result: Record<string, number> = {};
    for (let rev of arr) {
      for (let sq of selectionQuestions.questions) {
        const answer = rev.answers[sq.id];
        if (answer === 1) {
          if (!result[sq.id]) {
            result[sq.id] = 1;
          } else {
            result[sq.id]++;
          }
        }
      }
    }
    return result;
  }

  function anyPropertyReviewsExist() {
    return (
      Object.keys(propertyItemized).length +
        propertyAgg.length +
        propertyComments.length >
      0
    );
  }

  function hasItemizedResults(itemized: Record<string, number>) {
    return Object.keys(itemized).length > 0;
  }

  return (
    <div>
      {anyPropertyReviewsExist() ? (
        <div className="reviews-title">
          {
            <button
              className={
                "review-type-bttn " + (selected === "this" ? "selected" : "")
              }
              onClick={() => {
                setSelected("this");
                onOpen?.func();
              }}
            >
              reviews at this address
            </button>
          }
          {other_reviews!.length > 0 && (
            <button
              className={
                "review-type-bttn " + (selected === "other" ? "selected" : "")
              }
              onClick={() => {
                setSelected("other");
                onOpen?.func();
              }}
            >
              reviews at other addresses
            </button>
          )}
        </div>
      ) : (
        <div className="review-type-bttn selected">
          Reviews for other addresses owned by this landlord
        </div>
      )}
      <div className={"reviews-wrapper"}>
        {(selected === "this" ? propertyAgg : otherAgg).length > 0
          ? (selected === "this" ? propertyAgg : otherAgg).map(
              (question, i) => (
                <div className="review-section" key={i}>
                  <div className="review-question">
                    <Twemoji options={{ className: "small-emoji" }}>
                      {question.emoji} {question.question}
                    </Twemoji>
                  </div>
                  {question.answers.map((a, j) => (
                    <div className="review-answer" key={j}>
                      <span className="count">
                        {a.count} {a.count === 1 ? "person" : "people"} said:{" "}
                      </span>
                      <span className="statment">{a.text}</span>
                    </div>
                  ))}
                </div>
              )
            )
          : !hasItemizedResults(
              selected === "this" ? propertyItemized : otherItemized
            ) && (
              <div className="review-section">
                <div className="review-question">
                  {selected === "this"
                    ? "no reviews for this address"
                    : "no other reviews"}
                </div>
              </div>
            )}
        <div className="selection-questions">
          {hasItemizedResults(
            selected === "this" ? propertyItemized : otherItemized
          ) &&
            selectionQuestions.questions
              .filter(
                (sq) =>
                  (selected === "this" ? propertyItemized : otherItemized)[
                    sq.id
                  ]
              )
              .map((sq) => (
                <div key={sq.id} className="selection-question selection">
                  <Twemoji options={{ className: "small-emoji" }}>
                    {sq.emoji}
                  </Twemoji>{" "}
                  {sq.text}{" "}
                  <span className="item-count">
                    <small>x</small>
                    <strong>
                      {
                        (selected === "this"
                          ? propertyItemized
                          : otherItemized)[sq.id]
                      }
                    </strong>
                  </span>
                </div>
              ))}
        </div>
        {(selected === "this" ? propertyComments : otherComments).length !==
          0 && (
          <div>
            <div className="comment-title">comments:</div>
            {(selected === "this" ? propertyComments : otherComments).map(
              (comment, index) => (
                <p className="comment" key={index}>
                  <span className="date">
                    {new Date(comment.date).toLocaleDateString()}
                  </span>
                  <br />
                  {comment.text}
                </p>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Reviews;
